import { AfterViewInit, Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { heightContentAnimation, visibleContentAnimation, widthContentAnimation } from 'src/app/animations';

export type SupportWidgetState = 'collapsed' | 'tip' | 'expanded' | 'mia' | 'contact';

@Component({
  selector: 'app-support-widget',
  templateUrl: './support-widget.component.html',
  styleUrls: ['./support-widget.component.scss'],
  animations: [visibleContentAnimation, widthContentAnimation, heightContentAnimation]
})
export class SupportWidgetComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() miaSelected = new EventEmitter<void>();
  @Output() contactSelected = new EventEmitter<void>();
  @Output() fabClicked = new EventEmitter<void>();

  state: SupportWidgetState = 'collapsed';
  miaAvailable = false;
  contactAvailable = false;

  private readonly tipDismissedKey = 'myposventa_support_widget_tip_dismissed';
  private readonly scbToggleSelector = '.scb-widget-toggle';
  private readonly sitToggleSelector = '.sit-widget-toggle';
  private readonly scbThemePendingClass = 'scb-widget-toggle--theme-pending';
  private readonly sitThemePendingClass = 'sit-widget-toggle--theme-pending';
  private tipTimer: ReturnType<typeof setTimeout> | null = null;
  private tipTimer2: ReturnType<typeof setTimeout> | null = null;
  private externalWidgetObserver: MutationObserver | null = null;
  private externalWidgetObserverTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    if (!this.isTipDismissed()) {
      this.tipTimer = setTimeout(() => {
        if (this.state === 'collapsed') {
          this.state = 'tip';
        }
        this.tipTimer2 = setTimeout(() => {
          if (this.state === 'tip') {
            this.state = 'collapsed';
          }
        }, 10000);
      }, 1200);
    }
  }

  ngAfterViewInit(): void {
    this.setupExternalWidgetObserver();
  }

  ngOnDestroy(): void {
    if (this.tipTimer) {
      clearTimeout(this.tipTimer);
    }
    if (this.tipTimer2) {
      clearTimeout(this.tipTimer2);
    }
    if (this.externalWidgetObserverTimer) {
      clearTimeout(this.externalWidgetObserverTimer);
    }
    this.externalWidgetObserver?.disconnect();
    this.onBeforeUnload(new Event('beforeunload'));
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    sessionStorage.removeItem(this.tipDismissedKey);
  }

  @HostListener('window:message', ['$event'])
  onWindowMessage(event: MessageEvent): void {
    const type = event.data?.type;
    if (
      type === 'scb-launcher-css-vars' ||
      type === 'sit-launcher-css-vars' ||
      type === 'scb-launcher-state' ||
      type === 'sit-launcher-state'
    ) {
      this.refreshWidgetAvailability();
      this.syncStateFromExternalWidgets();
    }
  }

  get showTip(): boolean {
    return this.state === 'tip';
  }

  get showPanel(): boolean {
    return this.state === 'expanded';
  }

  get isExternalWidgetOpen(): boolean {
    return this.state === 'mia' || this.state === 'contact';
  }

  onFabClick(): void {
    this.fabClicked.emit();

    if (this.state === 'mia' || this.state === 'contact') {
      this.state = 'expanded';
      return;
    }

    if (this.state === 'expanded') {
      this.state = 'collapsed';
      return;
    }

    this.state = 'expanded';
  }

  dismissTip(event: Event): void {
    event.stopPropagation();
    this.state = 'collapsed';
    sessionStorage.setItem(this.tipDismissedKey, '1');
  }

  closePanel(): void {
    this.state = 'collapsed';
  }

  onMiaClick(): void {
    if (!this.miaAvailable) {
      return;
    }
    this.state = 'mia';
    this.miaSelected.emit();
  }

  onContactClick(): void {
    if (!this.contactAvailable) {
      return;
    }
    this.state = 'contact';
    this.contactSelected.emit();
  }

  private setupExternalWidgetObserver(retries = 60): void {
    this.refreshWidgetAvailability();

    const scbToggle = document.querySelector(this.scbToggleSelector);
    const sitToggle = document.querySelector(this.sitToggleSelector);

    if ((!scbToggle || !sitToggle) && retries > 0) {
      this.externalWidgetObserverTimer = setTimeout(
        () => this.setupExternalWidgetObserver(retries - 1),
        200
      );
    }

    this.externalWidgetObserver?.disconnect();
    this.externalWidgetObserver = new MutationObserver(() => {
      this.refreshWidgetAvailability();
      this.syncStateFromExternalWidgets();
    });

    if (scbToggle) {
      this.externalWidgetObserver.observe(scbToggle, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    if (sitToggle) {
      this.externalWidgetObserver.observe(sitToggle, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    this.syncStateFromExternalWidgets();
  }

  private refreshWidgetAvailability(): void {
    const scbToggle = document.querySelector(this.scbToggleSelector);
    const sitToggle = document.querySelector(this.sitToggleSelector);

    this.miaAvailable = this.isWidgetToggleReady(scbToggle, this.scbThemePendingClass);
    this.contactAvailable = this.isWidgetToggleReady(sitToggle, this.sitThemePendingClass);
  }

  private isWidgetToggleReady(toggle: Element | null, themePendingClass: string): boolean {
    return !!toggle && !toggle.classList.contains(themePendingClass);
  }

  private syncStateFromExternalWidgets(): void {
    const miaOpen = this.isInjectedWidgetOpen(this.scbToggleSelector);
    const contactOpen = this.isInjectedWidgetOpen(this.sitToggleSelector);

    if (miaOpen) {
      this.state = 'mia';
      return;
    }

    if (contactOpen) {
      this.state = 'contact';
      return;
    }

    if (
      (this.state === 'mia' && (!miaOpen || !this.miaAvailable)) ||
      (this.state === 'contact' && (!contactOpen || !this.contactAvailable))
    ) {
      this.state = 'collapsed';
    }
  }

  private isInjectedWidgetOpen(toggleSelector: string): boolean {
    const toggle = document.querySelector(toggleSelector);
    return !!toggle && !toggle.classList.contains('collapsed');
  }

  private isTipDismissed(): boolean {
    return sessionStorage.getItem(this.tipDismissedKey) === '1';
  }
}
